import { CustomField } from 'components/common/form/field/CustomField';
import { Page } from 'components/Page/Page';
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';
import i18n from 'i18n';
import { defaultTo, isEmpty, LoDashStatic } from 'lodash';
import { FormikField } from 'components/common/form/field/FormikField';
import { useCallAPI } from 'hooks/useCallAPI';
import { LoadingIndicator } from 'components/common/LoadingIndicator';
import { useFormikContext } from 'formik';
import { SpaceProductGroupFormValue } from 'core/limitation/ProductGroup';
import { DefaultProductGroupManager } from 'core/limitation/ProductGroupManager';
import {
  DefaultInventoryModel,
  Inventory
} from 'containers/Limitations/Inventory';
import { DefaultSelectComponentModel } from 'components/CampaignLimitation/SelectComponent';
import LimitationSet from 'components/CampaignLimitation/LimitationSet/LimitationSet';
import styles from './productGroupForm.module.scss';
import { Button } from 'react-bootstrap';
import { DefaultAdRequestSourceManager } from 'core/adRequestSource/AdRequestSourceManager';
import { useCoreContext } from 'contexts/coreContext';
import { isSystemAdmin } from 'helper/ActorHelper';
import { PMaxCore } from 'core';
import {
  DEFAULT_INVENTORY,
  LimitationInventorySettings
} from 'containers/Limitations/LimitationSetting/limitationConfig/limitationSettingsType';
import classNames from 'classnames';
import { ChannelGroupSetting } from 'core/channelGroup/ChannelGroupSettings';
import { DeliveryUnit } from 'core/channelGroup/ChannelGroup';
import { buildValueLabelMap } from 'utils/SelectOptionsUtils';
import {
  buildOutdoorDeliveryTargetOptions,
  getOutdoorDeliveryUnit,
  getOutdoorExtraGetter,
  getDeliveryUnitI18nKeys,
  toOutdoorHierarchyOptions
} from './utils';

const cx = classNames.bind(styles);

const productGroupManager = new DefaultProductGroupManager();
const adRequestSourceManager = new DefaultAdRequestSourceManager();
export const FilterSpaceSection: React.FC<{
  disable: boolean;
  adFormatAndChannelOptions: SelectOptions[];
  spaceAttributes: {
    [key: string]: { [attribute: string]: SelectOptions[] };
  };
  setAdFormatAndChannelOptions: Dispatch<SetStateAction<SelectOptions[]>>;
  setSpaceAttributes: Dispatch<
    SetStateAction<{
      [key: string]: { [attribute: string]: SelectOptions[] };
    }>
  >;
  onSpacesOptionsFetched: (spaceOptions: SelectOptions[]) => void;
  onOutdoorSpacesFetched: (spaces: SelectOptions[]) => void;
}> = ({
  disable,
  spaceAttributes,
  adFormatAndChannelOptions,
  setAdFormatAndChannelOptions,
  setSpaceAttributes,
  onSpacesOptionsFetched,
  onOutdoorSpacesFetched
}) => {
  const { values, setFieldValue } =
    useFormikContext<SpaceProductGroupFormValue>();
  const [needFetchSpacesOptions, setNeedFetchSpacesOptions] =
    useState<boolean>(false);
  const adFormat = defaultTo(values.adFormat, '');
  const channelGroupId = values.channelGroupId;
  const attributes = defaultTo(values.attributes, {});
  const [showInventory, setShowInventory] = useState<boolean>(false);
  const isOutdoor = adFormat === 'Outdoor';
  const deliveryUnit = getOutdoorDeliveryUnit(adFormat, values.channelGroupId);
  const isStoreUnit = deliveryUnit === DeliveryUnit.STORE;
  const isDeviceUnit = deliveryUnit === DeliveryUnit.DEVICE;
  const inventorySettings = useMemo(
    () =>
      productGroupManager.getSpaceAttributesInventorySettings(
        adFormat,
        channelGroupId,
        spaceAttributes,
        ['storeNum', 'nearbyPlaceLabel', 'deviceCharacteristic']
      ),
    [adFormat, channelGroupId, spaceAttributes]
  );

  // Per-attribute value→label maps from the freshly-fetched outdoor bag.
  // Outdoor labels come from server-side i18n via getOutdoorSpaceAttributes
  // (no more registered i18n keys), so saved attribute values look themselves
  // up here. Non-Outdoor still resolves via the legacy registered i18n keys.
  const outdoorAttributeLabelMaps = useMemo(() => {
    if (!isOutdoor) return {};
    const bag = spaceAttributes[`${adFormat}-${channelGroupId}`] ?? {};
    return Object.fromEntries(
      Object.entries(bag).map(([k, v]) => [k, buildValueLabelMap(v)])
    );
  }, [isOutdoor, adFormat, channelGroupId, spaceAttributes]);

  const resolveAttributeLabel = useCallback(
    (name: string, value: string | number) =>
      isOutdoor
        ? (outdoorAttributeLabelMaps[name]?.get(value) ?? String(value))
        : i18n.t(`productGroup.space.attributes.${name}.${value}`),
    [isOutdoor, outdoorAttributeLabelMaps]
  );

  const [inventory, setInventory] = useState<string>(DEFAULT_INVENTORY);
  const { loading, callAPIs } = useCallAPI();

  const core = useCoreContext() as PMaxCore;
  const isSysAdmin = isSystemAdmin(core.authenticationManager.actor);
  const addonFeatureManager = core.addonFeatureManager;

  // cache for outdoor spaces by channelGroupId
  const [outdoorSpacesCache, setOutdoorSpacesCache] = useState<{
    [channelGroupId: number]: SelectOptions[];
  }>({});

  useEffect(() => {
    if (adFormatAndChannelOptions.length === 0) {
      callAPIs(
        [
          () =>
            productGroupManager.getProductGroupValidAdFormatAndChannel(
              isSysAdmin,
              addonFeatureManager
            )
        ],
        (adFormatAndChannelOptions: SelectOptions[]) => {
          setAdFormatAndChannelOptions(adFormatAndChannelOptions);
        }
      );
    }
  }, [
    adFormatAndChannelOptions,
    isSysAdmin,
    addonFeatureManager,
    callAPIs,
    setAdFormatAndChannelOptions
  ]);

  useEffect(() => {
    needFetchSpacesOptions && onSpacesOptionsFetched([]);
  }, [needFetchSpacesOptions, onSpacesOptionsFetched]);

  useEffect(() => {
    if (adFormat === '' || channelGroupId === undefined) {
      return;
    }
    const channelDataOfAdFormat = adFormatAndChannelOptions.find(
      (option) => option.value === adFormat
    );
    const channelOptions = defaultTo(channelDataOfAdFormat?.options, []);
    if (
      channelOptions.find((option) => option.value === channelGroupId) ===
      undefined
    ) {
      return;
    }
    const cacheKey = `${adFormat}-${channelGroupId}`;
    if (!spaceAttributes[cacheKey]) {
      const fetchAttributes =
        adFormat === 'Outdoor'
          ? () =>
              adRequestSourceManager.getOutdoorSpaceAttributes(channelGroupId)
          : () =>
              adRequestSourceManager.getSpaceAttributes(
                adFormat,
                ChannelGroupSetting.fromId(channelGroupId)
              );
      callAPIs([fetchAttributes], (attributes) => {
        setSpaceAttributes((prev) => ({
          ...prev,
          [cacheKey]: attributes
        }));
      });
    }
  }, [
    adFormat,
    channelGroupId,
    adFormatAndChannelOptions,
    spaceAttributes,
    callAPIs,
    setSpaceAttributes
  ]);

  // fetch outdoor spaces when adFormat is Outdoor
  useEffect(() => {
    if (!spaceAttributes[`${adFormat}-${channelGroupId}`]) {
      onOutdoorSpacesFetched([]);
      return;
    }
    if (adFormat !== 'Outdoor' || channelGroupId === undefined) {
      onOutdoorSpacesFetched([]);
      return;
    }

    // check if already cached
    if (outdoorSpacesCache[channelGroupId]) {
      onOutdoorSpacesFetched(outdoorSpacesCache[channelGroupId]);
      return;
    }

    // fetch and cache
    const bag = spaceAttributes[`${adFormat}-${channelGroupId}`];
    const labelMap = buildValueLabelMap(
      (isStoreUnit ? bag.storeNum : bag.deviceCharacteristic) ?? []
    );
    callAPIs(
      [
        () =>
          adRequestSourceManager.getSpacesWithConditions(
            isSysAdmin,
            {},
            adFormat,
            channelGroupId,
            getOutdoorExtraGetter(isStoreUnit)
          )
      ],
      (spaces: SelectOptions[]) => {
        const options = toOutdoorHierarchyOptions(spaces, labelMap);
        setOutdoorSpacesCache((prev) => ({
          ...prev,
          [channelGroupId]: options
        }));
        onOutdoorSpacesFetched(options);
      }
    );
  }, [
    adFormat,
    channelGroupId,
    isSysAdmin,
    isStoreUnit,
    outdoorSpacesCache,
    spaceAttributes,
    callAPIs,
    onOutdoorSpacesFetched
  ]);

  const adFormatOptions = useMemo(() => {
    return adFormatAndChannelOptions.map((option) => ({
      label: option.label,
      value: option.value
    }));
  }, [adFormatAndChannelOptions]);

  const channelOptions = useMemo(() => {
    const target = adFormatAndChannelOptions.find(
      (option) => option.value === adFormat
    );
    return target ? defaultTo(target.options, []) : [];
  }, [adFormat, adFormatAndChannelOptions]);

  const updateAttributes = useCallback(
    (newValue: { [key: string]: string[] }) => {
      setNeedFetchSpacesOptions(true);
      setFieldValue('attributes', newValue);
    },
    [setFieldValue]
  );

  const resetAttributes = useCallback(() => {
    setInventory(DEFAULT_INVENTORY);
    updateAttributes({});
  }, [updateAttributes]);

  const onAdFormatChange = useCallback(
    (adFormat: string) => {
      const target = adFormatAndChannelOptions.find(
        (option) => option.value === adFormat
      );
      const supportedChannels = target ? defaultTo(target.options, []) : [];
      const isSupport =
        supportedChannels.find((option) => option.value === channelGroupId) !==
        undefined;
      if (!isSupport) {
        setFieldValue(
          'channel',
          supportedChannels.length > 0 ? supportedChannels[0].value : ''
        );
      }
      resetAttributes();
    },
    [channelGroupId, adFormatAndChannelOptions, setFieldValue, resetAttributes]
  );

  const onDeleteAttributes = useCallback(
    (type: string) => {
      let limitationdelete = attributes[type];
      if (limitationdelete) {
        delete attributes[type];
        updateAttributes({ ...attributes });
      }
    },
    [attributes, updateAttributes]
  );

  const onAttributesChange = useCallback(
    (type: string, newValue: SelectOptions[] | LoDashStatic) => {
      if (!Array.isArray(newValue)) {
        return;
      }

      if (newValue.length === 0) {
        onDeleteAttributes(type);
        return;
      }

      updateAttributes({
        ...attributes,
        [type]: newValue.map((option) => option.value.toString())
      });
    },
    [attributes, updateAttributes, onDeleteAttributes]
  );

  const querySpaceOptionsByFilters = useCallback(() => {
    setNeedFetchSpacesOptions(false);
    const bag = spaceAttributes[`${adFormat}-${channelGroupId}`] ?? {};
    const labelMap = isOutdoor
      ? buildValueLabelMap(
          (isStoreUnit ? bag.storeNum : bag.deviceCharacteristic) ?? []
        )
      : undefined;
    callAPIs(
      [
        () =>
          adRequestSourceManager.getSpacesWithConditions(
            isSysAdmin,
            attributes,
            adFormat,
            channelGroupId,
            isOutdoor ? getOutdoorExtraGetter(isStoreUnit) : undefined
          )
      ],
      (options: SelectOptions[]) => {
        if (!isOutdoor) {
          onSpacesOptionsFetched(options);
          return;
        }
        onSpacesOptionsFetched(
          buildOutdoorDeliveryTargetOptions(options, {
            isStoreUnit,
            isDeviceUnit,
            labelMap
          })
        );
      }
    );
  }, [
    adFormat,
    isOutdoor,
    isStoreUnit,
    isDeviceUnit,
    attributes,
    channelGroupId,
    isSysAdmin,
    callAPIs,
    onSpacesOptionsFetched,
    spaceAttributes
  ]);

  const inventoryModel = useMemo(() => {
    return new DefaultInventoryModel(
      inventory,
      inventorySettings,
      {},
      'productGroupForm.labels.attributes',
      (newInventory) => setInventory(newInventory)
    );
  }, [inventory, inventorySettings]);

  const renderInventoryComponent = useCallback(
    (inventorySettings: LimitationInventorySettings[]) => {
      return inventorySettings.map((inventorySetting) => {
        const {
          name,
          singleSelect,
          itemSetting,
          searchPlaceholder,
          component: Component
        } = inventorySetting;

        if (!Component) {
          return <div key={name} />;
        }

        const value = defaultTo(attributes[name], []).map((value) => ({
          label: resolveAttributeLabel(name, value),
          value
        }));
        const model = new DefaultSelectComponentModel(
          name,
          singleSelect,
          itemSetting,
          i18n.t(searchPlaceholder),
          (newValue) => onAttributesChange(name, newValue),
          [],
          value,
          undefined,
          disable
        );

        return <Component key={name} model={model} />;
      });
    },
    [disable, attributes, onAttributesChange, resolveAttributeLabel]
  );

  const renderFilterSet = useCallback(
    (inventorySettings: LimitationInventorySettings[]) => {
      const ignoreDefault = ({ name }: LimitationInventorySettings) =>
        name !== DEFAULT_INVENTORY;
      return inventorySettings.filter(ignoreDefault).map(({ name, title }) => {
        const value: SelectOptions[] = defaultTo(attributes[name], []).map(
          (value) => ({
            label: resolveAttributeLabel(name, value),
            value
          })
        );
        const handleAdd = () => setInventory(name);
        const onChange = (newValue) => onAttributesChange(name, newValue);
        const onDelete = (_, type) => onDeleteAttributes(type);
        return (
          <LimitationSet
            key={name}
            value={value}
            title={title}
            type={name}
            disable={disable || !showInventory}
            add={handleAdd}
            onChange={onChange}
            deleteLimitation={onDelete}
          />
        );
      });
    },
    [
      disable,
      showInventory,
      attributes,
      onDeleteAttributes,
      onAttributesChange,
      resolveAttributeLabel
    ]
  );

  const onCollapseButtonClick = useCallback(() => {
    setShowInventory((prev) => !prev);
  }, []);

  const collapseButtonTitle = useMemo(() => {
    return showInventory
      ? i18n.t('productGroupForm.buttons.finishEdit')
      : i18n.t('productGroupForm.buttons.editFilters');
  }, [showInventory]);

  const onlyShowTitle =
    !showInventory &&
    !adFormat &&
    channelGroupId === undefined &&
    isEmpty(attributes);
  const showAdFormat = showInventory || adFormat;
  const showChannel = showInventory || channelGroupId !== undefined;
  const showAttributes = showInventory || !isEmpty(attributes);
  return (
    <Page.Section
      title={i18n.t('productGroupForm.title.filterSection')}
      inlineTitle
      className={cx({ [styles.sectionOnlyTitle]: onlyShowTitle })}
      withShadow={false}
    >
      {loading && <LoadingIndicator />}
      <div className={styles.collapseButton} onClick={onCollapseButtonClick}>
        {collapseButtonTitle}
      </div>
      {showAdFormat && (
        <FormikField.Select
          labelColSm={2}
          inputColSm={10}
          label={i18n.t('productGroupForm.labels.adFormat')}
          name='adFormat'
          simpleValue
          options={adFormatOptions}
          onChange={onAdFormatChange}
          disabled={disable || !showInventory}
        />
      )}
      {showChannel && (
        <FormikField.Select
          labelColSm={2}
          inputColSm={10}
          label={i18n.t('productGroupForm.labels.channel')}
          name='channelGroupId'
          simpleValue
          options={channelOptions}
          onChange={resetAttributes}
          disabled={disable || !showInventory}
        />
      )}
      {showAttributes && (
        <CustomField
          labelColSm={2}
          inputColSm={10}
          label={i18n.t('productGroupForm.labels.attributes')}
          name={'attributes'}
        >
          <div style={{ display: 'flex' }}>
            {showInventory && (
              <div style={{ marginRight: 40 }}>
                <Inventory
                  width={438}
                  height={338}
                  sidebarWidth={160}
                  model={inventoryModel}
                >
                  {renderInventoryComponent(inventorySettings)}
                </Inventory>
              </div>
            )}
            <div className={styles.selectedFilters}>
              {renderFilterSet(inventorySettings)}
            </div>
          </div>
        </CustomField>
      )}
      {showInventory && (
        <CustomField
          labelColSm={2}
          inputColSm={10}
          label=''
          name='filterButton'
        >
          <Button
            style={{ marginTop: '24px' }}
            variant='primary'
            size='sm'
            disabled={disable}
            onClick={querySpaceOptionsByFilters}
          >
            {i18n.t(
              getDeliveryUnitI18nKeys(isStoreUnit, isDeviceUnit).filterButton
            )}
          </Button>
        </CustomField>
      )}
    </Page.Section>
  );
};
